import { CSSProperties, FC } from 'react'
import Modal from 'react-modal'
import { RotateCcw } from 'lucide-react'

interface CompletionModalProps {
	isOpen: boolean
	rounds: number
	totalTimeLabel: string
	onRepeat: () => void
	onCloseToMain: () => void
}

const confettiPieces = [
	{ left: '6%', delay: '0s', duration: '2.8s', drift: '-18px' },
	{ left: '14%', delay: '0.4s', duration: '3.2s', drift: '22px' },
	{ left: '24%', delay: '0.9s', duration: '2.6s', drift: '-24px' },
	{ left: '36%', delay: '0.2s', duration: '2.9s', drift: '26px' },
	{ left: '48%', delay: '0.7s', duration: '3.1s', drift: '-20px' },
	{ left: '58%', delay: '0.1s', duration: '2.7s', drift: '28px' },
	{ left: '70%', delay: '1.1s', duration: '3.4s', drift: '-16px' },
	{ left: '81%', delay: '0.6s', duration: '2.5s', drift: '18px' },
	{ left: '91%', delay: '0.3s', duration: '3s', drift: '-22px' },
]

const calculateApproxCalories = (rounds: number) => Math.max(1, Math.round(rounds * 5.8))

export const CompletionModal: FC<CompletionModalProps> = ({
	isOpen,
	rounds,
	totalTimeLabel,
	onRepeat,
	onCloseToMain,
}) => {
	const calories = calculateApproxCalories(rounds)

	return (
		<Modal
			isOpen={isOpen}
			shouldCloseOnEsc={false}
			shouldCloseOnOverlayClick={false}
			style={{
				overlay: {
					backgroundColor: 'rgba(12, 16, 22, 0.62)',
					zIndex: 50,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					padding: '0 18px',
				},
				content: {
					position: 'relative',
					inset: 'unset',
					width: '100%',
					maxWidth: 470,
					border: 'none',
					borderRadius: 32,
					padding: '24px 18px 18px',
					backgroundColor: '#fff',
					overflow: 'visible',
				},
			}}>
			<style>
				{`@keyframes timer-confetti-fall {
					0% { transform: translate3d(0,-26px,0) rotate(0deg); opacity: 0; }
					12% { opacity: 1; }
					100% { transform: translate3d(var(--drift), 250px, 0) rotate(220deg); opacity: 0; }
				}`}
			</style>
			<div className='relative flex flex-col items-center'>
				<div
					data-testid='timer-completion-confetti'
					className='pointer-events-none absolute inset-0 z-[4] overflow-visible'>
					{confettiPieces.map((piece, index) => (
						<span
							key={`${piece.left}-${index}`}
							className='absolute top-[-44px] h-[14px] w-[7px] rounded-sm bg-[#D0EB00] opacity-90'
							style={
								{
									left: piece.left,
									animation: `timer-confetti-fall ${piece.duration} linear ${piece.delay} infinite`,
									'--drift': piece.drift,
								} as CSSProperties
							}
						/>
					))}
				</div>

				<div
					data-testid='timer-completion-check-badge'
					className='absolute -top-[62px] z-20 h-[118px] w-[118px] rounded-full bg-[#D6F20C] p-[7px] shadow-[0_10px_24px_rgba(141,170,0,0.28)]'>
					<div className='flex h-full w-full items-center justify-center rounded-full border-2 border-[#C6E00A] text-[72px] leading-none text-black'>
						✓
					</div>
				</div>

				<h3 className='mt-10 text-center text-[24px] font-semibold leading-tight text-black'>
					Тренировка завершена!
				</h3>
				<p className='mt-1 text-center text-[15px] text-[#636363]'>Отличная работа</p>

				<div className='mt-4 grid w-full grid-cols-3 rounded-[18px] border border-[#ECECEC] bg-[#F7F7F7] px-2 py-3'>
					<div className='border-r border-[#E2E2E2] px-2 text-center'>
						<div className='text-[11px] text-[#6D6D6D]'>Раунды</div>
						<div className='mt-1 whitespace-nowrap text-[24px] font-semibold leading-none text-black'>
							{rounds}
						</div>
					</div>
					<div className='border-r border-[#E2E2E2] px-2 text-center'>
						<div className='text-[11px] text-[#6D6D6D]'>Общее время</div>
						<div className='mt-1 whitespace-nowrap text-[24px] font-semibold leading-none text-black'>
							{totalTimeLabel}
						</div>
					</div>
					<div className='px-2 text-center'>
						<div className='text-[11px] text-[#6D6D6D]'>Калории*</div>
						<div className='mt-1 whitespace-nowrap text-[24px] font-semibold leading-none text-black'>
							{calories}
						</div>
						<div className='text-[10px] text-[#6D6D6D]'>*Примерно</div>
					</div>
				</div>

				<button
					type='button'
					onClick={onRepeat}
					className='mt-4 flex h-[44px] w-[92%] items-center justify-center gap-2 rounded-[15px] bg-[#D2F300] text-[20px] font-semibold text-black transition hover:brightness-95'>
					Повторить
					<RotateCcw aria-hidden='true' className='size-5' />
				</button>
				<button
					type='button'
					onClick={onCloseToMain}
					className='mt-2 h-[44px] w-[92%] rounded-[15px] border border-[#9E9E9E] bg-white text-[18px] font-medium text-black transition hover:bg-[#F8F8F8]'>
					На главный экран
				</button>
			</div>
		</Modal>
	)
}
